import React, { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import { safeJsonParse } from '../../utils/jsonUtils'
import Paper from '@mui/material/Paper'
import Fab from '@mui/material/Fab'
import IconButton from '@mui/material/IconButton'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import SendIcon from '@mui/icons-material/Send'
import { experimentalStyled as styled } from '@mui/material/styles'
import toast from 'react-hot-toast'
import TextField from '@mui/material/TextField'
import { ClockLoader } from 'react-spinners'
import axios from '../../api/axios'
import socket from '../../socket'
import { getDateTimeWithWeekday } from '../../utils/dateUtils'

const Item = styled(Paper)(({ theme }) => ({
   backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
   ...theme.typography.body2,
   padding: '20px 20px 20px 20px',
   height: '70vh',
   display: 'flex',
   flexDirection: 'column',
   gap: '10px',
   textAlign: 'center',
   borderLeft: '5px solid #FA870B',
   color: theme.palette.text.secondary,
}))

export default function Support() {
   const [account,setAccount] = useState(null);
   const messagesEndRef = useRef(null)
   const inputRef = useRef(null)
   const [myID, setMyID] = useState('')
   const [name, setName] = useState('')
   const [supportID, setSupportID] = useState('')
   const [chattingMsg, setChattingMsg] = useState('')
   const [allMsg, setAllMsg] = useState([])
   const [chatBoxWidth, setChatBoxWidth] = useState(null)
   const [fileData, setFileData] = useState({})
   const [fileOpen, setFileOpen] = useState(false)
   const [isLoading, setIsLoading] = useState(false)
   const inputElement = useRef('fileInput')


   useEffect(()=>{
      const user = localStorage.getItem('user');
      if(user){
         let parse = safeJsonParse(user);
         setAccount(parse);
      }
   },[]);


   const scrollToBottom = () => {
      messagesEndRef.current.scrollIntoView({
         behavior: 'smooth',
         block: 'start',
      })
   }

   const getSupportID = async () => {
      try {
         await axios
            .post(`getSupportID`)
            .then(async (result) => {
               if (result.data.status) await setSupportID(result.data.data)
            })
      } catch (error) {
         if (process.env.REACT_APP_MODE) console.log(error)
         toast.error('Ett fel uppstod. Försök igen.')
      }
   }

   const sendChatting = async () => {
      if (!isLoading) {
         setIsLoading(true)
         try {
            const date = await getCustomDate()
            let data = {}
            let flag
            if (fileData?.name) {
               data = {
                  from: myID,
                  to: supportID,
                  msg: '',
                  date: date,
                  status: false,
               }
               flag = true
            } else {
               data = {
                  from: myID,
                  to: supportID,
                  msg: chattingMsg,
                  date: date,
                  status: false,
               }
               flag = false
            }
            if (chattingMsg.trim() || fileData?.name) {
               if (fileData?.size / 1000000 > 5) {
                  toast.error(`Can't upload 2MB files`)
                  return
               }
               if (flag) {
                  let params = new FormData()
                  params.append('file', fileData)
                  params.append('data', JSON.stringify(data))
                  await axios
                     .post(
                        `sendToSupport`,
                        params
                     )
                     .then(async (result) => {
                        if (result.data.status) {
                           data.msg = result.data.data
                           await setAllMsg([...allMsg, data])
                        } else {
                           toast.error(result.data.data)
                        }
                     })
               } else {
                  socket.emit('sendToSupport', { data, name })
                  await setAllMsg([...allMsg, data])
               }
            } else {
               toast.error('Write the message')
            }
            if (flag) {
               setFileData(null)
               setFileOpen(false)
               inputElement.current.value = null
            } else {
               setChattingMsg('')
               inputRef.current.focus()
            }
            setIsLoading(false)
         } catch (error) {
            if (process.env.REACT_APP_MODE) console.log(error)
         toast.error('Ett fel uppstod. Försök igen.')
         }
      } else {
         toast.error('Loading...')
      }
   }

   const getKeyCode = async (e) => {
      if (e.key === 'Enter' && !e.ctrlKey && !e.altKey && !e.shiftKey) {
         await sendChatting()
      }
   }

   const getCustomDate = getDateTimeWithWeekday

   const getChattingHistory = async (id) => {
      try {
         await axios
            .post(`getChattingHistory`, {
               id: id,
            })
            .then(async (result) => {
               if (result.data.status) {
                  await setAllMsg(result.data.data)
               }
            })
      } catch (error) {
         if (process.env.REACT_APP_MODE) console.log(error)
         toast.error('Ett fel uppstod. Försök igen.')
      }
   }

   const handleFileload = () => {
      inputElement.current.click()
   }

   const getFile = async (e) => {
      setFileData(e.target.files[0])
      setChattingMsg('')
      setFileOpen(true)
   }

   const deleteFile = () => {
      setFileData(null)
      setFileOpen(false)
   }

   const getWidth = () =>
      window.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth

   useEffect(() => {
      const setResponsiveness = () => {
         setChatBoxWidth(getWidth() - getWidth() / 2)
      }
      setResponsiveness()
      window.addEventListener('resize', setResponsiveness)
      return () => window.removeEventListener('resize', setResponsiveness)
   }, [])

   useEffect(() => {
      if (fileData?.name) setFileOpen(true)
      else setFileOpen(false)
   }, [fileData])

   useEffect(() => {
      if (account) {
         setMyID(account?._id)
         setName(account?.name)
         getChattingHistory(account?._id)
         getSupportID()
      }
   }, [account])

   useEffect(() => {
      socket.on(myID, async (e) => {
         await setAllMsg([...allMsg, e.data])
      })
      setTimeout(() => {
         scrollToBottom()
      }, 100)
      return () => {
         socket.off('connect')
         socket.off('disconnect')
         socket.off(myID)
      }
   }, [allMsg, myID])

   return (
      <Box
         sx={{
            flexGrow: 1,
            p: 3,
            bgcolor: 'rgb(229, 229, 229)',
            overflowY: 'overlay',
         }}
      >
         <Box sx={{ mt: '80px' }}>
            <Grid
               container
               spacing={{ xs: 2, md: 3 }}
               columns={{ xs: 4, sm: 8, md: 12 }}
               paddingX={'8vw'}
               paddingY={'2vh'}
            >
               <Grid item xs={12} sm={12} md={12}>
                  <Box color={'#FA870B'} fontSize={'25px'}>
                     Support Area | Chat Section
                  </Box>
                  <Item>
                     <Grid
                        container
                        justifyContent="space-between"
                        alignItems="flex-end"
                        paddingX={'2vw'}
                        height={'94%'}
                        fontSize={'16px'}
                        style={{
                           overflowY: 'auto',
                        }}
                     >
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                           {allMsg.map((item, ind) => {
                              const arr = item.msg.split('->')
                              const type = arr[arr.length - 1]
                              return (
                                 <Box
                                    key={ind}
                                    textAlign={
                                       myID === item.from ? 'right' : 'left'
                                    }
                                    color={
                                       myID === item.from
                                          ? '#FA870B'
                                          : '#e10000'
                                    }
                                    className="chatting-group"
                                    position={'relative'}
                                 >
                                    <Box>
                                       {type === 'file' ? (
                                          <p
                                             className="chatting-msg"
                                             style={{
                                                width: chatBoxWidth,
                                             }}
                                          >
                                             <a
                                                style={{
                                                   color: `${
                                                      myID === item.from
                                                         ? 'blue'
                                                         : 'green'
                                                   }`,
                                                }}
                                                href={`${
                                                   process.env
                                                      .REACT_APP_BASE_URL
                                                }chatFile/${
                                                   arr[arr.length - 2]
                                                }`}
                                                download={arr[0]}
                                             >
                                                {arr[0]}
                                             </a>
                                          </p>
                                       ) : (
                                          <p
                                             className="chatting-msg"
                                             style={{
                                                width: chatBoxWidth,
                                                whiteSpace: 'pre-wrap',
                                             }}
                                          >
                                             {item.msg}
                                          </p>
                                       )}
                                       {myID === item.from ? (
                                          <p className="chatting-right-date">
                                             {item.date}
                                          </p>
                                       ) : (
                                          <p className="chatting-left-date">
                                             {item.date}
                                          </p>
                                       )}
                                    </Box>
                                 </Box>
                              )
                           })}
                           <div ref={messagesEndRef} />
                        </Grid>
                     </Grid>
                     <Grid
                        container
                        justifyContent={'space-between'}
                        alignItems={'flex-end'}
                     >
                        <Grid item xs={0.5}>
                           <input
                              ref={inputElement}
                              type="file"
                              style={{ display: 'none' }}
                              onChange={(e) => getFile(e)}
                           />
                           <IconButton
                              color="primary"
                              component="label"
                              onClick={handleFileload}
                           >
                              <AttachFileIcon />
                           </IconButton>
                        </Grid>
                        {fileOpen ? (
                           <Grid className="select-file" item xs={10}>
                              <Box className="file-group-box">
                                 <Box className="upload-img-box">
                                    <UploadFileIcon className="upload-img-icon" />
                                 </Box>
                                 <Box className="file-info">
                                    <Box>
                                       <Box className="filename-string">
                                          {fileData?.name}
                                       </Box>
                                       <Box>
                                          {(fileData?.size / 1000000).toFixed(
                                             4
                                          )}{' '}
                                          MB
                                       </Box>
                                    </Box>
                                 </Box>
                              </Box>
                              <Box className="close-img-box">
                                 <HighlightOffIcon
                                    className="close-img-icon"
                                    onClick={() => deleteFile()}
                                 />
                              </Box>
                           </Grid>
                        ) : (
                           <Grid item xs={10}>
                              <TextField
                                 id="filled-multiline-flexible"
                                 ref={inputRef}
                                 label="Type something"
                                 multiline
                                 variant="filled"
                                 minRows={1}
                                 maxRows={3}
                                 fullWidth
                                 value={chattingMsg}
                                 onKeyUp={(e) => getKeyCode(e)}
                                 onChange={(e) =>
                                    setChattingMsg(e.target.value)
                                 }
                              />
                           </Grid>
                        )}
                        <Grid item xs={1}>
                           <Fab
                              color="secondary"
                              aria-label="edit"
                              onClick={() => {
                                 sendChatting()
                              }}
                           >
                              {isLoading ? (
                                 <ClockLoader color="#fff" size={30} />
                              ) : (
                                 <SendIcon />
                              )}
                           </Fab>
                        </Grid>
                     </Grid>
                  </Item>
               </Grid>
            </Grid>
         </Box>
      </Box>
   )
}
